import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAtomValue } from 'jotai'
import { twMerge } from 'tailwind-merge'
import { PreviewCard } from '@highlight/ui'
import type { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card'
import { useGlobalState } from '@highlight/ipc/react'
import { oppositeSide } from '@shared/sidepanel/side'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { topEntry } from '@renderer/overlay/Sidepanel/view-history'
import { HOME_VIEW_ID, useViewScope } from '@renderer/overlay/Sidepanel/view-state-atoms'
import { useIsHostedViewOpen, useSidepanelSide } from '../../atoms'
import { ViewContext } from '../../view-context'
import { PreviewCardShell } from '@renderer/components/preview-card-shell'
import { PreviewPillButton } from '@renderer/components/preview-card-shell/pill-button'
import { markPreviewOpen, requestHoverChange, useHoverGroupWarm } from './hover-group'
import { useSafeTriangle } from './use-safe-triangle'
import { isInRect, type Point } from './safe-triangle'
import { ConeBridge } from './nested/cone-bridge'
import { PreviewFeedbackPill } from './nested/feedback-pill'

/**
 * Hover preview card for a sidepanel cell — full metadata and CTAs without
 * leaving the list (HIG-5838).
 *
 * The trigger is unstyled so the wrapped cell keeps its own look; the card is
 * PreviewCard's popover surface.
 *
 * Base UI supplies the deliberate open delay; the safe triangle on top of it
 * keeps the card up — and neighbouring rows shut — while the pointer travels
 * diagonally from cell to card.
 */

type PreviewCardChangeDetails = BasePreviewCard.Root.ChangeEventDetails

const OPEN_DELAY_MS = 600

/** Amplitude `trigger` for events initiated from inside the hover card — see HOVER_CLUSTER_TRIGGER. */
export const HOVER_CARD_TRIGGER = 'hover-card'

interface CellPreviewCardProps {
  /** The cell itself. Rendered inside a real block element — never display:contents, which has no rect to hover. */
  children: ReactNode
  /** Card contents. Mounted only while open. */
  preview: ReactNode
  /** Suppresses the card entirely (context menu open, cell not in a hoverable state, …). */
  disabled?: boolean
  triggerClassName?: string
  popupClassName?: string
  openDelay?: number
  /** `tight` is the reminder card's closer padding (Figma 8478:20165). */
  density?: 'default' | 'tight'
}

const TIGHT_POPUP_CLASS = 'gap-3 p-3'

type PinAdjust = (delta: 1 | -1) => void

const PinContext = createContext<PinAdjust>(() => {})

/**
 * Holds the card open while a popover launched from inside it is open.
 *
 * Base UI only tracks the pointer against the card's own rect, so a nested
 * popover (snooze picker, feedback comment) — which portals to its own anchored
 * surface, often extending past the card — reads as "pointer left" and would
 * close the card out from under itself.
 */
export function useCellPreviewPin(open: boolean) {
  const adjust = useContext(PinContext)
  const wasOpenRef = useRef(false)

  // Layout, not effect: a nested popover's open and the card's hover-close can
  // land in the same click. Waiting until after paint lets the card unmount
  // before pinCount increments, which is the flicker on the date/time picker.
  useLayoutEffect(() => {
    if (open === wasOpenRef.current) {
      return
    }
    wasOpenRef.current = open
    adjust(open ? 1 : -1)
  }, [open, adjust])

  useEffect(() => {
    return () => {
      if (!wasOpenRef.current) {
        return
      }
      wasOpenRef.current = false
      adjust(-1)
    }
  }, [adjust])
}

/**
 * Open state for a popover rendered inside a card, pinned automatically.
 *
 * Prefer this over pairing `useState` with `useCellPreviewPin` by hand: forgetting
 * the pin closes the card out from under the popover, and nothing type-checks that.
 *
 * Pin count is adjusted in the setter (same tick as open) so a click that both
 * opens the nested surface and looks like a hover-leave to the card still
 * keeps the card up.
 */
export function usePinnedDisclosure(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false)
  const adjust = useContext(PinContext)
  const openRef = useRef(false)

  const setPinnedOpen = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (openRef.current === next) {
        return
      }
      openRef.current = next
      adjust(next ? 1 : -1)
    },
    [adjust],
  )

  useEffect(() => {
    return () => {
      if (!openRef.current) {
        return
      }
      openRef.current = false
      adjust(-1)
    }
  }, [adjust])

  return [open, setPinnedOpen]
}

function CellPreviewCard({
  children,
  preview,
  disabled = false,
  triggerClassName,
  popupClassName,
  openDelay = OPEN_DELAY_MS,
  density = 'default',
}: CellPreviewCardProps) {
  const dockSide = useSidepanelSide()
  const [hoverOpen, setHoverOpen] = useState(false)
  const [pinCount, setPinCount] = useState(0)
  const triggerRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const owner = useContext(ViewContext)
  const scope = useViewScope()
  const activeEntry = topEntry(useAtomValue(scope.history))
  // A View open beside the panel sits where this card would; only cards inside it still show.
  const coveredByHostedView = useIsHostedViewOpen() && !scope.hosted
  const ownerActive = owner ? owner.entryKey === activeEntry.key : activeEntry.view.id === HOME_VIEW_ID

  const adjustPin = useCallback((delta: 1 | -1) => setPinCount((count) => Math.max(0, count + delta)), [])

  // The card portals outside the panel, and the panel leaves without the pointer
  // moving — so no leave event fires and the card would sit alone on screen once
  // the panel is gone. Swipe-to-close is tracked separately from panel state
  // because it only flips that state once the dismiss spring lands; state alone
  // leaves the card floating over empty screen for the length of the animation.
  const [panelSwiping] = useGlobalState<boolean>('sidepanel.isSwiping', false)
  const panelVisible = useSidepanelState() === 'open' && !panelSwiping

  const surfaceVisible = panelVisible && ownerActive
  const open = surfaceVisible && !disabled && !coveredByHostedView && (hoverOpen || pinCount > 0)

  // Reset rather than only suppressing: hover and pin state survive visibility
  // changes and would otherwise snap the card back up when its owner returns.
  useEffect(() => {
    if (surfaceVisible) {
      return
    }
    setHoverOpen(false)
    setPinCount(0)
  }, [surfaceVisible])

  // The popup is portaled, so it does not move with the row that anchors it.
  // Dismiss it as soon as any scroll container moves rather than leaving a card
  // behind after its row has scrolled away.
  useEffect(() => {
    if (!open) {
      return
    }

    const handleScroll = (event: Event) => {
      // Only a scroller strictly containing the trigger can displace the row
      // the card is anchored to. That rules out scrollers inside the card or
      // trigger (the tag row's nav arrows drive one), and nested popovers —
      // they portal to body, and both their own scrollers and the
      // document-level scroll their mount can emit fire without moving the
      // anchor; dismissing on those unmounts them before they paint.
      const target = event.target
      if (!(target instanceof Element) || target === triggerRef.current || !target.contains(triggerRef.current)) {
        return
      }
      setHoverOpen(false)
      setPinCount(0)
    }

    // Native scroll events do not bubble; capture sees scrolls from the
    // sidepanel's nested list containers as well as document scrolling.
    document.addEventListener('scroll', handleScroll, true)
    return () => document.removeEventListener('scroll', handleScroll, true)
  }, [open])

  // Reading down a list shouldn't cost the delay on every row — once one card is
  // up, the rest follow the pointer immediately.
  const id = useId()
  const warm = useHoverGroupWarm()

  // A warm swap replaces the card, so the outgoing one is cut instead of faded.
  // The shared popup surface only animates on close. `instant` flips per
  // transition, not per warm window.
  const [instant, setInstant] = useState(false)

  const closeHover = useCallback(() => setHoverOpen(false), [])
  const { onTriggerPointerLeave, onTriggerPointerMove, bridgedCone } = useSafeTriangle({
    id,
    open,
    triggerRef,
    popupRef,
    onLeave: closeHover,
  })

  // Replayed at the point where another card's trip was given up. That point
  // may no longer be over this row.
  const openIfUnderPointer = useCallback((at: Point) => {
    const trigger = triggerRef.current
    if (!trigger || !isInRect(at, trigger.getBoundingClientRect())) {
      return
    }
    setInstant(true)
    setHoverOpen(true)
  }, [])

  const handleOpenChange = useCallback(
    (nextOpen: boolean, details: PreviewCardChangeDetails) => {
      if (details.reason === 'trigger-hover' && !requestHoverChange(id, nextOpen, openIfUnderPointer)) {
        details.cancel()
        return
      }
      setInstant(nextOpen && warm)
      setHoverOpen(nextOpen)
    },
    [warm, id, openIfUnderPointer],
  )

  // Registering also dismisses whichever card was showing: instant opens outrun
  // our own close delay, so otherwise the outgoing card would sit on screen
  // beside the incoming one. A pinned card holds regardless — `open` keeps it up
  // while a popover of its own is showing.
  useEffect(() => {
    if (!open) {
      return
    }
    return markPreviewOpen(id, () => {
      setInstant(true)
      setHoverOpen(false)
    })
  }, [open, id])

  return (
    <PreviewCard open={open} onOpenChange={handleOpenChange}>
      <PreviewCard.Trigger
        unstyled
        delay={warm ? 0 : openDelay}
        render={<div ref={triggerRef} />}
        onPointerMove={onTriggerPointerMove}
        onPointerLeave={onTriggerPointerLeave}
        data-preview-owner-inactive={ownerActive ? undefined : ''}
        className={twMerge('block w-full', triggerClassName)}
      >
        {children}
      </PreviewCard.Trigger>
      <PreviewCard.Portal>
        {/* Opposite the docked edge, so the card floats over the screen rather than off it. */}
        <PreviewCard.Positioner
          side={oppositeSide(dockSide)}
          align="center"
          sideOffset={dockSide === 'left' ? 12 : 8}
          collisionPadding={16}
        >
          <PreviewCard.Popup
            ref={popupRef}
            className={twMerge(
              'w-[400px] max-w-[400px] gap-4 overflow-visible p-4',
              instant && 'transition-none',
              density === 'tight' && TIGHT_POPUP_CLASS,
              popupClassName,
            )}
          >
            <PinContext.Provider value={adjustPin}>{preview}</PinContext.Provider>
          </PreviewCard.Popup>
        </PreviewCard.Positioner>
      </PreviewCard.Portal>
      {bridgedCone && <ConeBridge cone={bridgedCone} />}
    </PreviewCard>
  )
}

CellPreviewCard.Shell = PreviewCardShell
CellPreviewCard.PillButton = PreviewPillButton
CellPreviewCard.FeedbackPill = PreviewFeedbackPill

export { CellPreviewCard }
